import React, { useMemo, useRef, useEffect, useState } from 'react'
import { use, init, getInstanceByDom } from 'echarts/core'
import {
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  HeatmapChart,
  RadarChart,
  BoxplotChart,
} from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  VisualMapComponent,
  GraphicComponent,
  TitleComponent,
  MarkLineComponent,
  AxisPointerComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { isEmpty, isFunction, isNil, debounce, isEqualWith } from 'lodash'

import { CHART, resolveChartColors } from '@/components/charts/chartPalette'
import { ensureChartTheme } from '@/components/charts/chartTheme'
import { useTheme } from '@/utils/hooks/useTheme'

// This is the only echarts entry point in ui/, so this registry must cover
// every series type and option-level component any consumer's option uses —
// an unregistered type fails at runtime, not at build time. The root
// 'echarts' import registered everything as a side effect and cost ~8MB of
// bundle; keep this list in sync with actual usage instead.
use([
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  HeatmapChart,
  RadarChart,
  BoxplotChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  VisualMapComponent,
  GraphicComponent,
  TitleComponent,
  MarkLineComponent,
  AxisPointerComponent,
  CanvasRenderer,
])

// Consumers rebuild their option object inline on every render, and a notMerge
// setOption tears the whole chart down — including any open tooltip, which
// stays gone until the pointer moves again. Structurally identical options are
// skipped instead. Functions (formatters) are recreated closures each render,
// so any two are treated as equal; a formatter whose behavior changes with no
// other structural change won't trigger an update.
const sameOption = (previous, next) =>
  isEqualWith(previous, next, (previousValue, nextValue) =>
    isFunction(previousValue) && isFunction(nextValue) ? true : undefined
  )

const getLoadingOptions = () =>
  resolveChartColors({
    maskColor: CHART.LOADING_MASK,
    textColor: CHART.TEXT_MUTED,
    color: CHART.BRAND,
  })

/**
 * Create a div that injects the echarts canvas via ref
 * @url https://echarts.apache.org/en/index.html
 * @example https://echarts.apache.org/examples/en/index.html
 *
 * @param {Object}  option  contains all the configuration for any chart. See examples for more info
 * @param {Object}  style   override CSS for the div
 * @param {Boolean} loading toggle loading overlay on the chart
 * @param {Object}  events  event listeners for the chart in the form of { event: handlerFn }
 *
 * @returns HTMLElement<div>
 */
export const EChart = ({
  option,
  style = {},
  loading = false,
  events = {},
  progress,
  duration,
  ...props
}) => {
  const chartSettings = { useCoarsePointer: true } // enables clicking near a line and still highlighting it
  const optionSettings = { notMerge: true } // don't merge two options together when updating option
  const chartRef = useRef(null)
  // Listeners are bound once at mount, so they read handlers through this ref
  // to avoid closing over stale props/state. The event names themselves are
  // still fixed at mount.
  const eventsRef = useRef(events)
  eventsRef.current = events
  const appliedOptionRef = useRef(null)
  const theme = useTheme()
  // Re-theming disposes and rebuilds the chart, so a page with many charts only
  // rebuilds the ones on screen; the rest catch up when they scroll into view.
  const [appliedTheme, setAppliedTheme] = useState(theme)
  const [isVisible, setIsVisible] = useState(false)

  const getCursorValue = (timestamp) => {
    const chart = getInstanceByDom(chartRef.current)
    const x = chart.convertToPixel({ xAxisIndex: 0 }, timestamp)
    const y = chart.getHeight()

    return {
      graphic: [
        {
          id: 'cursor',
          type: 'line',
          invisible: false,
          zlevel: 10,
          z: 1000,
          silent: true,
          style: { lineWidth: 1, stroke: CHART.TEXT, opacity: 1 },
          shape: { x1: x, y1: 0, x2: x, y2: y },
        },
      ],
    }
  }

  // Debounce resize event so it only fires periodically instead of constantly
  const resizeChart = useMemo(
    () =>
      debounce(() => {
        if (chartRef.current) {
          const chart = getInstanceByDom(chartRef.current)
          // resize: https://echarts.apache.org/en/api.html#echartsInstance.resize
          chart.resize()
        }
      }, 50),
    []
  )

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting))
    observer.observe(chartRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (isVisible) {
      setAppliedTheme(theme)
    }
  }, [isVisible, theme])

  useEffect(() => {
    // Initialize chart
    let chart
    if (chartRef.current) {
      // init: https://echarts.apache.org/en/api.html#echarts.init
      chart = init(chartRef.current, ensureChartTheme(appliedTheme), chartSettings)
      // A fresh instance has no option yet, so the effects below re-apply theirs.
      appliedOptionRef.current = null

      // Set up event listeners
      for (const key of Object.keys(events)) {
        chart.on(key, (param) => {
          eventsRef.current[key]?.(param, chart)
        })
      }
    }

    // Resize event listener
    const resizeObserver = new ResizeObserver(() => {
      resizeChart()
    })

    resizeObserver.observe(chartRef.current)

    // Return cleanup function
    return () => {
      if (!isEmpty(events)) {
        for (const [key] of Object.entries(events)) {
          if (chart && isFunction(chart.off)) {
            chart.off(key)
          }
        }
      }

      // dispose: https://echarts.apache.org/en/api.html#echartsInstance.dispose
      if (chart && isFunction(chart.dispose)) {
        chart.dispose()
      }

      if (chartRef.current) {
        resizeObserver.unobserve(chartRef.current)
      }
      resizeObserver.disconnect()
    }
  }, [appliedTheme])

  useEffect(() => {
    // Re-render chart when option changes
    if (chartRef.current && !sameOption(appliedOptionRef.current, option)) {
      appliedOptionRef.current = option
      const chart = getInstanceByDom(chartRef.current)
      // setOption: https://echarts.apache.org/en/api.html#echartsInstance.setOption
      chart.setOption(resolveChartColors(option), optionSettings)
    }
  }, [option, appliedTheme])

  useEffect(() => {
    if (chartRef.current) {
      // getInstanceByDom: https://echarts.apache.org/en/api.html#echarts.getInstanceByDom
      const chart = getInstanceByDom(chartRef.current)

      if (loading) {
        // loading: https://echarts.apache.org/en/api.html#echartsInstance.showLoading
        chart.showLoading('default', getLoadingOptions())
      } else {
        chart.hideLoading()
      }
    }
  }, [loading, appliedTheme])

  useEffect(() => {
    if (chartRef.current && progress && !isNil(duration)) {
      const chart = getInstanceByDom(chartRef.current)
      chart.setOption(resolveChartColors(getCursorValue(progress)), {
        notMerge: false,
        replaceMerge: ['graphic'],
      })
    }
  }, [progress, appliedTheme])

  return (
    <div
      data-testid="echart"
      ref={chartRef}
      style={{ width: '100%', height: '350px', ...style }}
      {...props}
    />
  )
}
